import { useEffect, useRef, useCallback } from 'react';

interface UseInactivityTimeoutOptions {
  timeoutMs: number;
  onTimeout: () => void;
  enabled?: boolean;
}

/**
 * Última actividad del usuario en CUALQUIER pestaña del admin. La sesión de Supabase es una sola
 * para todas las pestañas (localStorage) y el `signOut` de una cierra todas: sin esto, una pestaña
 * olvidada cerraba la sesión a la hora mientras el usuario trabajaba en otra (p. ej. en un ticket).
 */
export const ACTIVIDAD_COMPARTIDA_KEY = 'sozu_admin_ultima_actividad';
// Mover el mouse dispara decenas de eventos por segundo: se escribe a lo más cada 5 s.
const CADA_ESCRITURA_MS = 5000;

function leerActividadCompartida(): number | null {
  try {
    const v = Number(localStorage.getItem(ACTIVIDAD_COMPARTIDA_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

function marcarActividadCompartida(ts: number) {
  try {
    localStorage.setItem(ACTIVIDAD_COMPARTIDA_KEY, String(ts));
  } catch {
    /* sin storage: el timer de esta pestaña sigue funcionando solo */
  }
}

/**
 * Hook that triggers a callback after a period of user inactivity.
 * Tracks mouse, keyboard, scroll, and touch events, shared across tabs.
 */
export function useInactivityTimeout({
  timeoutMs,
  onTimeout,
  enabled = true
}: UseInactivityTimeoutOptions) {
  const timeoutRef = useRef<number | null>(null);
  const onTimeoutRef = useRef(onTimeout);
  const ultimaActividadRef = useRef(Date.now());
  const ultimaEscrituraRef = useRef(0);

  // Keep callback ref updated to avoid stale closures
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const vencerRef = useRef<() => void>(() => {});
  const armar = useCallback((ms: number) => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => vencerRef.current(), Math.max(ms, 0));
  }, []);

  // Al vencer, antes de cerrar se mira si otra pestaña tuvo actividad después: en ese caso se
  // adopta y se re-arma con el tiempo que le queda.
  const vencer = useCallback(() => {
    const otra = leerActividadCompartida();
    if (otra && otra > ultimaActividadRef.current) ultimaActividadRef.current = otra;
    const restante = timeoutMs - (Date.now() - ultimaActividadRef.current);
    if (restante > 1000) {
      armar(restante);
      return;
    }
    onTimeoutRef.current();
  }, [timeoutMs, armar]);
  vencerRef.current = vencer;

  const resetTimer = useCallback(() => {
    if (!enabled) return;
    const ahora = Date.now();
    ultimaActividadRef.current = ahora;
    if (ahora - ultimaEscrituraRef.current >= CADA_ESCRITURA_MS) {
      ultimaEscrituraRef.current = ahora;
      marcarActividadCompartida(ahora);
    }
    armar(timeoutMs);
  }, [timeoutMs, enabled, armar]);

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Events that indicate user activity. En fase de captura: algunos componentes (Radix, editores)
    // detienen la propagación del teclado y escribir no contaba como actividad.
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'input',
      'wheel',
      'scroll',
      'touchstart',
      'click'
    ];

    const handleActivity = () => resetTimer();
    // Al volver a la pestaña: el timer de una pestaña en segundo plano se retrasa (throttling),
    // así que se re-evalúa con la actividad compartida.
    const handleVisible = () => {
      if (document.visibilityState === 'visible') vencer();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true, capture: true });
    });
    document.addEventListener('visibilitychange', handleVisible);

    // Start timer
    resetTimer();

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, { capture: true });
      });
      document.removeEventListener('visibilitychange', handleVisible);
    };
  }, [enabled, resetTimer, vencer]);

  return { resetTimer };
}
